export const REVENUE_REFRESH_NOTE =
  'Already on the other venues. Sharing again only refreshes the multiplier, VAT, and order.'
const REVENUE_CATEGORY_REFRESHED = 'Refreshed the multiplier, VAT, and order.'
const REVENUE_CATEGORY_TAKEN_OFF =
  'Taken off so the shared categories replace it.'

const venueWord = count => `${count} ${count === 1 ? 'venue' : 'venues'}`

export const revenueCategoryReceiptLines = results => {
  const order = []
  const counts = new Map()
  for (const row of results || []) {
    for (const line of row?.snapshot?.lines || []) {
      if (!line?.name || line.outcome !== 'updated') continue
      let tally = counts.get(line.name)
      if (!tally) {
        tally = { added: 0, updated: 0, taken: 0 }
        counts.set(line.name, tally)
        order.push(line.name)
      }
      if (line.reason === REVENUE_CATEGORY_TAKEN_OFF) tally.taken += 1
      else if (line.reason === REVENUE_CATEGORY_REFRESHED) tally.updated += 1
      else if (!line.reason) tally.added += 1
    }
  }
  return order.flatMap(name => {
    const tally = counts.get(name)
    const lines = []
    if (tally.added) lines.push(`${name} added to ${venueWord(tally.added)}.`)
    if (tally.updated)
      lines.push(`${name} updated on ${venueWord(tally.updated)}.`)
    if (tally.taken) lines.push(`${name} taken off ${venueWord(tally.taken)}.`)
    return lines
  })
}

export const revenueRefreshOnly = results => {
  const rows = results || []
  if (rows.length === 0) return false
  if (rows.some(row => row.outcome === 'failed' || row.outcome === 'skipped'))
    return false
  const lines = rows.flatMap(row => row.snapshot?.lines || [])
  if (lines.length === 0) return false
  return lines.every(
    line =>
      line.outcome === 'updated' && line.reason === REVENUE_CATEGORY_REFRESHED,
  )
}

export const runProgressText = run => {
  const counts = run?.counts || {}
  const done = counts.done || 0
  const total = counts.total || 0
  if (run?.status === 'finished') {
    return `Finished. ${counts.updated || 0} updated, ${
      counts.skipped || 0
    } skipped, ${counts.failed || 0} failed.`
  }
  if (!total) return 'Starting.'
  const label = run?.progress || 'queued'
  return `${label}. ${done} of ${total} venues.`
}

export const runStartFailureMessage = error =>
  error?.response?.data?.message || error?.message || 'The run did not finish.'

const csvCell = value => `"${String(value || '').replace(/"/g, '""')}"`

export const resultLeftIngredientsOff = result =>
  (result?.missingIngredients || []).length > 0 ||
  /missing ingredients/i.test(result?.reason || '')

export const missingIngredientLine = ({
  venue,
  recipe,
  ingredients,
  reason,
}) => {
  const why =
    reason ||
    (ingredients?.length
      ? `Missing ingredients: ${ingredients.join(', ')}.`
      : '')
  return [venue, recipe, why].filter(Boolean).join('. ')
}

const countKey = row =>
  JSON.stringify({
    receiving: row?.receiving || 0,
    chosen: row?.chosen || 0,
    landing: [...(row?.landing || [])].sort(),
  })

const everyVenueLabel = (count, excludesSource) =>
  excludesSource ? `The other ${count} venues` : `All ${count} venues`

export const menuRecipeCountLines = (
  counts,
  excludesSource = false,
  nothingCanLand = false,
  gapsListed = false,
) => {
  if (nothingCanLand) return []
  const rows = gapsListed
    ? (counts || []).filter(row => (Number(row.receiving) || 0) > 0)
    : counts
  return recipeCountLines(rows, excludesSource)
}

export const recipeCountLines = (counts, excludesSource = false) => {
  const rows = counts || []
  if (rows.length === 0) return []
  const groups = []
  for (const row of rows) {
    const key = countKey(row)
    const group = groups.find(item => item.key === key)
    if (group) group.rows.push(row)
    else groups.push({ key, rows: [row] })
  }
  return groups.map(group => {
    const row = group.rows[0]
    const names = (row.landing || []).join(', ')
    const who =
      group.rows.length === rows.length && rows.length > 1
        ? everyVenueLabel(rows.length, excludesSource)
        : group.rows.length > 1
        ? (() => {
            const phrase = venuePhrase(
              group.rows.map(item => item.title),
              rows.length,
              excludesSource,
            )
            return phrase.charAt(0).toUpperCase() + phrase.slice(1)
          })()
        : row.title
    return `${who} will get ${row.receiving} of ${row.chosen} recipes.${
      names ? ` ${names}.` : ''
    }`
  })
}

const venuePhrase = (venues, venueCount, excludesSource) => {
  if (venues.length === venueCount && venueCount > 1) {
    return excludesSource
      ? `the other ${venueCount} venues`
      : `all ${venueCount} venues`
  }
  if (venues.length <= 1) return venues[0] || ''
  if (venues.length === 2) return `${venues[0]} and ${venues[1]}`
  return `${venues.slice(0, -1).join(', ')}, and ${venues[venues.length - 1]}`
}

export const ingredientGapLines = (
  gaps,
  forceShare = false,
  excludesSource = false,
) => {
  const rows = gaps || []
  if (rows.length === 0) return []
  const venueCount = new Set(rows.map(row => row.title)).size
  const groups = new Map()
  for (const row of rows) {
    for (const item of row.recipes || []) {
      const key = JSON.stringify({
        recipe: item.recipe || '',
        section: item.section || '',
        ingredients: [...(item.ingredients || [])].sort(),
      })
      const group = groups.get(key) || { item, venues: [] }
      if (!group.venues.includes(row.title)) group.venues.push(row.title)
      groups.set(key, group)
    }
  }
  return [...groups.values()].map(({ item, venues }) => {
    const who = venuePhrase(venues, venueCount, excludesSource)
    if (forceShare) {
      const titled = who.charAt(0).toUpperCase() + who.slice(1)
      return missingIngredientLine({
        venue: titled,
        recipe: item.recipe,
        reason: `Copied with ingredients left off: ${(
          item.ingredients || []
        ).join(', ')}.`,
      })
    }
    const section = item.section || ''
    const sectionPhrase = !section
      ? ''
      : /,| and /.test(section)
      ? `the ${section} sections`
      : `the ${section} section`
    const place = sectionPhrase ? `${sectionPhrase} at ${who}` : `at ${who}`
    return `${item.recipe} will be left off ${place}. Missing ${(
      item.ingredients || []
    ).join(', ')}.`
  })
}

const problemResults = results =>
  (results || []).filter(
    result =>
      result.outcome === 'skipped' ||
      result.outcome === 'failed' ||
      resultLeftIngredientsOff(result),
  )

const REVENUE_LEFT_OUT = new Set([
  'another account',
  'already has it',
  'same name',
  'renamed',
  'switched off',
])

const supplierOffCount = result => {
  const fromLines = (result?.skippedLines || []).filter(line =>
    /supplier is not on this venue/i.test(line?.reason || ''),
  ).length
  if (fromLines) return fromLines
  return (
    String(result?.reason || '').match(/Supplier is not on this venue/g) || []
  ).length
}

const catalogueOffCount = result => {
  const fromLines = (result?.skippedLines || []).filter(line =>
    /not in this venue's catalogue/i.test(line?.reason || ''),
  ).length
  if (fromLines) return fromLines
  return (
    String(result?.reason || '').match(
      /Product code is not in this venue's catalogue/g,
    ) || []
  ).length
}

export const shoppingListLeftLinesOff = result => catalogueOffCount(result) > 0

/** After a share finishes — anything skipped/failed should send people to Misses. */
export const shareResultsHaveMisses = note => {
  if ((Number(note?.skipped) || 0) > 0 || (Number(note?.failed) || 0) > 0)
    return true
  return (note?.results || []).some(
    result =>
      result?.outcome === 'skipped' ||
      result?.outcome === 'failed' ||
      resultLeftIngredientsOff(result) ||
      shoppingListLeftLinesOff(result),
  )
}

export const GROUP_MISSES_TAB_QUERY = 'misses'

export const groupMissesPath = groupId =>
  groupId ? `/venue-groups/${groupId}?tab=${GROUP_MISSES_TAB_QUERY}` : ''

const leftOffSentence = count => {
  const noun = count === 1 ? 'line was' : 'lines were'
  return `${count} ${noun} left off: product is not in the catalogue.`
}

const supplierNameList = names => {
  const list = [
    ...new Set(
      (names || []).map(name => String(name || '').trim()).filter(Boolean),
    ),
  ]
  if (list.length <= 1) return list[0] || ''
  if (list.length === 2) return `${list[0]} and ${list[1]}`
  return `${list.slice(0, -1).join(', ')}, and ${list.at(-1)}`
}

export const restrictedSupplierReceiptLines = results =>
  (results || [])
    .map(result => {
      const title = result.venueTitle || result.title || 'Venue'
      const landed = supplierNameList(result.landed)
      const missingNames = (result.skippedLines || [])
        .filter(line => /not on this venue/i.test(line?.reason || ''))
        .map(line => line.label || line.supplierTitle)
      const missing = supplierNameList(missingNames)
      if (landed && missing) {
        const verb = missingNames.length === 1 ? 'is' : 'are'
        return `${title}. ${landed} restricted. ${missing} ${verb} not on this venue.`
      }
      if (landed) return `${title}. ${landed} restricted.`
      if (missing) {
        const verb = missingNames.length === 1 ? 'is' : 'are'
        return `${title}. Nothing restricted. ${missing} ${verb} not on this venue.`
      }
      if (result.outcome === 'skipped' || result.outcome === 'failed') {
        return missingIngredientLine({
          venue: title,
          reason: result.reason || result.outcome,
        })
      }
      return ''
    })
    .filter(Boolean)

export const shoppingListReceiptLines = results =>
  (results || [])
    .map(result => {
      const title = result.venueTitle || 'Venue'
      const leftOff = catalogueOffCount(result)
      if (result.outcome === 'updated') {
        if (!leftOff) return `${title} updated.`
        return `${title}. Updated. ${leftOffSentence(leftOff)}`
      }
      const blocked = supplierOffCount(result)
      if (result.outcome === 'failed' && blocked > 0) {
        const noun = blocked === 1 ? 'line' : 'lines'
        const names = [
          ...new Set(
            [
              ...(result.supplierNames || []),
              ...(result.skippedLines || [])
                .filter(line =>
                  /supplier is not on this venue/i.test(line?.reason || ''),
                )
                .map(line => String(line.supplierTitle || '').trim()),
            ].filter(Boolean),
          ),
        ]
        const who =
          names.length === 0
            ? 'supplier is not switched on.'
            : names.length === 1
            ? `${names[0]} is not switched on.`
            : `${names.slice(0, -1).join(', ')} and ${names.at(
                -1,
              )} are not switched on.`
        return `${title}. Nothing landed. ${blocked} ${noun}: ${who}`
      }
      if (result.outcome === 'skipped' || result.outcome === 'failed') {
        return missingIngredientLine({
          venue: title,
          reason: result.reason || result.outcome,
        })
      }
      return ''
    })
    .filter(Boolean)

export const receiptProblemLines = results => {
  const rows = problemResults(results)
  const venues = [...new Set(rows.map(result => result.venueTitle || 'Venue'))]
  const groups = new Map()
  for (const result of rows) {
    const recipe = result.recipeTitle || result.snapshot?.title || ''
    const reason = result.reason || result.outcome
    const key = `${recipe}\0${reason}`
    const group = groups.get(key) || []
    group.push(result)
    groups.set(key, group)
  }
  const lines = []
  for (const group of groups.values()) {
    const recipe = group[0].recipeTitle || group[0].snapshot?.title || ''
    const reason = group[0].reason || group[0].outcome
    if (REVENUE_LEFT_OUT.has(reason)) {
      group.forEach(result => {
        const category = result.recipeTitle ? ` of ${result.recipeTitle}` : ''
        lines.push(
          `${result.venueTitle || 'Venue'} was left out${category}: ${reason}.`,
        )
      })
      continue
    }
    const names = [
      ...new Set(group.map(result => result.venueTitle || 'Venue')),
    ]
    const sameForEveryVenue =
      venues.length > 1 &&
      names.length === venues.length &&
      group.length === venues.length
    if (sameForEveryVenue) {
      lines.push(
        missingIngredientLine({
          venue: `All ${venues.length} venues`,
          recipe,
          reason,
        }),
      )
    } else {
      group.forEach(result => {
        lines.push(
          missingIngredientLine({
            venue: result.venueTitle || 'Venue',
            recipe,
            reason,
          }),
        )
      })
    }
  }
  return lines
}

export const failureReceiptCsv = (results, action) => {
  const lines = ['line']
  const rows =
    action === 'apply_shopping_list'
      ? shoppingListReceiptLines(results).filter(
          line => !line.endsWith(' updated.'),
        )
      : receiptProblemLines(results)
  rows.forEach(line => lines.push(csvCell(line)))
  return lines.join('\n')
}

export const downloadFailureReceipt = (results, action) => {
  const csv = failureReceiptCsv(results, action)
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'venue-group-failures.csv'
  link.click()
  URL.revokeObjectURL(url)
}
